import { FC, useEffect, useMemo, useState } from 'react';
import { App, Col, Form, Row, Select, Table, Tag } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { EllipsisOutlined } from '@ant-design/icons';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { deleteFaq, fetchFaqList } from 'services/api/faq';
import { FaqListRow } from 'types/api/faq';
import { fetchServices } from 'services/api/services';
import { ServiceListRow } from 'types/api/services';

import useOrgEffect from 'hooks/useOrgEffect';
import { LanguageSwitcher } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import CreateFaq from './components/CreateFaq';
import { showErrorMessage } from 'utils/showErrorMessage';
import UpdateFaq from './components/UpdateFaq';

const PAGE_LIMIT = 10;

const FAQ: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { modal, message } = App.useApp();
  const lang = useMemo(() => searchParams.get('lang'), [searchParams]);
  const page = Number(searchParams.get('page') || 1);
  const service = searchParams.get('service');
  const [updateVisibleId, setUpdateVisibleId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [services, setServices] = useState<ServiceListRow[]>([]);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchServices({
          page: 1,
          limit: 999,
          lang: lang ?? undefined
        });

        if (!cancelled) setServices(res.results);
      } catch {
        // swallow — empty options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const [faq, setFaq] = useState<FaqListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!lang) return;
    let cancelled = false;

    const load = async () => {
      try {
        setIsFetching(true);
        const res = await fetchFaqList(
          {
            page,
            limit: PAGE_LIMIT,
            ...(service ? { service_id: Number(service) } : {})
          },
          lang
        );

        if (cancelled) return;
        setFaq(res.data?.data ?? []);
        setTotal(res.data?.total ?? 0);
      } catch {
        // swallow — empty table on failure
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [lang, service, page, refreshKey]);

  const triggerRefresh = () => setRefreshKey(k => k + 1);

  useEffect(() => {
    if (!lang) {
      searchParams.set('lang', 'en');
      setSearchParams(searchParams);
    }
  }, [lang, searchParams, setSearchParams]);

  const onServiceChange = (value: string) => {
    if (value) {
      searchParams.set('service', value);
    } else {
      searchParams.delete('service');
    }

    searchParams.set('page', '0');
    setSearchParams(searchParams);
  };

  const onClickDelete = (id: string) => {
    modal.confirm({
      title: 'Warning',
      content: <p>Are you sure?</p>,
      onOk: async () => {
        try {
          await deleteFaq(id);
          message.success('Successfully deleted');
          triggerRefresh();
        } catch (err) {
          showErrorMessage(err);
        }
      }
    });
  };

  const onCloseUpdateModal = () => {
    setUpdateVisibleId('');
  };

  return (
    <PageWrapper
      title="FAQ"
      extra={[
        <LanguageSwitcher lang={`${lang}`} key="LanguageSwitcher" />,
        <CreateFaq
          maxSortIndex={total + 1}
          onSuccess={triggerRefresh}
          key="CreateFaq"
        />
      ]}
      color="white"
    >
      <Row>
        <Col sm={8}>
          <Form.Item>
            <Select
              allowClear
              style={{ width: 220 }}
              placeholder="Select service"
              size="large"
              onChange={onServiceChange}
              value={service}
              options={services.map(item => ({
                label: item.title,
                value: item.id
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Table
        dataSource={faq}
        columns={[
          {
            title: 'Display Order',
            key: 'sort_index',
            dataIndex: 'sort_index',
            width: '15%',
            align: 'center',
            render: value => (
              <Tag style={{ paddingBlock: 6, paddingInline: 12 }} color="blue">
                {value ? value : 'Not set'}
              </Tag>
            )
          },
          {
            title: 'ID',
            key: 'id',
            dataIndex: 'id',
            width: '10%'
          },
          {
            title: 'Question',
            key: 'question',
            dataIndex: 'question',
            width: '45%'
          },
          {
            title: 'Answer',
            key: 'answer',
            dataIndex: 'answer',
            width: '45%'
          },
          {
            title: '',
            key: 'action',
            dataIndex: 'id',
            render: id => (
              <ActionMenu
                data={[
                  {
                    title: 'Edit',
                    permission: {
                      route: PERMISSION_ROUTES.faq,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      setUpdateVisibleId(id);
                    },
                    menuProps: {
                      style: {
                        color: 'var(--primary)'
                      }
                    }
                  },
                  {
                    title: 'Delete',
                    permission: {
                      route: PERMISSION_ROUTES.faq,
                      action: PERMISSION_ACTIONS.delete
                    },
                    action() {
                      onClickDelete(id);
                    },
                    menuProps: {
                      style: {
                        color: 'var(--danger-color)'
                      }
                    }
                  }
                ]}
                placement="bottomRight"
                icon={<EllipsisOutlined />}
              />
            )
          }
        ]}
        loading={isFetching}
        rowKey="id"
        pagination={{
          total,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          },
          showSizeChanger: false
        }}
      />
      {!!updateVisibleId && (
        <UpdateFaq
          maxSortIndex={total + 1}
          id={updateVisibleId}
          onClose={onCloseUpdateModal}
          onSuccess={triggerRefresh}
        />
      )}
    </PageWrapper>
  );
};

export default FAQ;
